import { IOrgProperty, OrgProperties } from "@/shared/types"
import { useGlobalStore } from "@/store"

type OrgPropertyResponseEntry = {
  key?: OrgProperties | string
  propertyKey?: OrgProperties | string
  value?: unknown
}

const normalizeOrgPropertyValue = (value: unknown): string | Record<string, unknown> | null => {
  if (value == null) {
    return null
  }

  if (typeof value === "string") {
    return value
  }

  if (typeof value === "object") {
    return value as Record<string, unknown>
  }

  return String(value)
}

const normalizeOrgPropertyEntry = (entry: unknown): IOrgProperty | null => {
  if (!entry || typeof entry !== "object") {
    return null
  }

  const record = entry as OrgPropertyResponseEntry
  const key = (record.key ?? record.propertyKey) as OrgProperties | undefined

  if (!key) {
    return null
  }

  return {
    key,
    value: normalizeOrgPropertyValue(record.value),
  }
}

export const normalizeOrgPropertiesResponse = (data: unknown): IOrgProperty[] => {
  if (Array.isArray(data)) {
    return data
      .map(normalizeOrgPropertyEntry)
      .filter((entry): entry is IOrgProperty => entry !== null)
  }

  if (data && typeof data === "object") {
    return Object.entries(data as Record<string, unknown>)
      .map(([key, value]) => normalizeOrgPropertyEntry({ key, value }))
      .filter((entry): entry is IOrgProperty => entry !== null)
  }

  return []
}

export const getOrgPropertyRawValue = (
  properties: IOrgProperty[] | undefined,
  propertyKey: OrgProperties,
): unknown => properties?.find((prop) => prop.key === propertyKey)?.value ?? null

export const getOrgPropertyStringValue = (
  properties: IOrgProperty[] | undefined,
  propertyKey: OrgProperties,
): string => {
  const value = getOrgPropertyRawValue(properties, propertyKey)

  if (value == null) {
    return ""
  }

  if (typeof value === "string") {
    return value
  }

  if (typeof value === "object") {
    return JSON.stringify(value)
  }

  return String(value)
}

export const getOrgProperties = (keys: OrgProperties[]): (string | null)[] => {
  const orgProperties: IOrgProperty[] = useGlobalStore.getState().orgProperties

  return keys.map((key) => {
    const value = getOrgPropertyStringValue(orgProperties, key)
    return value || null
  })
}

export const getBooleanOrgProperty = (key: OrgProperties): boolean => {
  const orgProperties: IOrgProperty[] = useGlobalStore.getState().orgProperties

  return getOrgPropertyStringValue(orgProperties, key) === "true"
}
